import React from "react";
import { Box } from "@mui/material";
import { useTheme } from "../context/ThemeContext";
import LogoMark from "../assets/LogoMark.png";
import LogoFallback from "../assets/LogoNew2.png";

/**
 * OTS Logo mark.
 *
 * The artwork is the original logo exactly as designed — LogoMark.png is the
 * shipped logo cropped to its silhouette with only the alpha channel kept.
 * That silhouette is used as a CSS mask, so the shape stays pixel-identical
 * while the paint behind it comes from the active theme.
 *
 * @param {object} props
 * @param {number} props.height - rendered height in px
 * @param {string} props.color - solid colour / custom CSS background; defaults
 *                               to the theme's primary→secondary gradient
 * @param {object} props.sx - extra MUI styles
 */

// Intrinsic proportions of the mark (192 x 381)
const ASPECT = 192 / 381;

const OtsLogo = ({ height = 40, color, sx = {}, ...rest }) => {
  const { theme } = useTheme();

  const paint =
    color ||
    `linear-gradient(150deg, ${theme.primary} 0%, ${theme.secondary} 100%)`;

  return (
    <Box
      component="span"
      role="img"
      aria-label="OTS"
      sx={{
        display: "inline-block",
        flexShrink: 0,
        height,
        width: height * ASPECT,
        background: paint,
        WebkitMaskImage: `url(${LogoMark})`,
        maskImage: `url(${LogoMark})`,
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        transition: "background 0.3s ease",
        // Browsers without CSS masking still get the original logo
        '@supports not ((-webkit-mask-image: url("")) or (mask-image: url("")))':
          {
            background: `url(${LogoFallback}) center / contain no-repeat`,
          },
        ...sx,
      }}
      {...rest}
    />
  );
};

export default OtsLogo;
